import { useEffect, useMemo, useRef, useState } from "react";
import { Briefcase, ChevronRight, Loader2, LogOut, Moon, Search, Settings as SettingsIcon, Shield, X } from "lucide-react";
import { Avatar } from "@/components/shared/Avatar";
import { api } from "@/lib/api";
import { searchProjects, searchPeople } from "@/components/mobile/globalSearch";

// Menu varianty B (194): přes celou obrazovku, vyjede zprava. Nahoře hledání, pod ním
// moduly ve skupinách jako levý panel na PC (z navFor). Zavírá křížek, Esc, tah doprava
// i systémové Zpět na Androidu (vlastní položka v historii).
// 349: účet (karta „já“, Nastavení, Superadmin, tmavý režim, odhlášení) je jen v okně
// profilu pod avatarem — tady byl podruhé. Najde je ale hledání („tmavý“, „odhlásit“).

const KW = {
  "/": "domů úvod přehled",
  "/tabule": "živě kdo pracuje mapa",
  "/zakazky": "stavby projekty",
  "/dochazka": "příchod odchod hodiny výkaz volno dovolená",
  "/denik": "stavební deník zápis",
  "/galerie": "fotky foto",
  "/sklad": "materiál zásoby",
  "/planovani": "harmonogram plán fáze gantt",
  "/ukoly": "práce úkol",
  "/ekonomika/doklady": "faktury peníze náklady doklady účtenky",
  "/tym": "lidé zaměstnanci",
  "/investori": "klienti zákazníci",
  "/nastaveni": "účet profil heslo",
  "/moje-faktury": "faktury",
};
const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
// 200: zakázky a lidé pro hledání — načtou se až při prvním písmenu, na 2 minuty
// v paměti (klíč: uživatel + firma, ať se po přepnutí firmy nemíchají)
let SEARCH_CACHE = { key: null, at: 0, projects: null, people: null };
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// peopleSearch: lidi hledá jen vedení (karta člověka /tym/:id je jen pro vedení)
export default function MenuB({ open, focusSearch, onClose, onNavigate, items, isItemActive, user,
  showSettings, onSettings, showSuperadmin, onSuperadmin, dark, onToggleDark, onLogout, peopleSearch = false }) {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const swipe = useRef(null);
  const swiped = useRef(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [found, setFound] = useState({ projects: null, people: null });
  const hasQ = !!q.trim();
  const cacheKey = `${user?.id || ""}|${user?.company_id || ""}|${peopleSearch ? 1 : 0}`;
  useEffect(() => {
    if (!open || !hasQ) return undefined;
    const c = SEARCH_CACHE;
    if (c.key === cacheKey && c.projects && Date.now() - c.at < 120000) { setFound({ projects: c.projects, people: c.people }); return undefined; }
    let alive = true;
    setFound({ projects: null, people: null });
    const list = (r) => (Array.isArray(r.data) ? r.data : []);
    Promise.all([
      api.get("/projects/options").then(list).catch(() => []),
      peopleSearch ? api.get("/team").then(list).catch(() => []) : Promise.resolve([]),
    ]).then(([projects, people]) => {
      SEARCH_CACHE = { key: cacheKey, at: Date.now(), projects, people };
      if (alive) setFound({ projects, people });
    });
    return () => { alive = false; };
  }, [open, hasQ, cacheKey, peopleSearch]);

  // vjezd / odjezd (při zavření ještě 260 ms animace, pak odpojit)
  useEffect(() => {
    if (open) { setMounted(true); setClosing(false); setQ(""); return undefined; }
    if (!mounted) return undefined;
    setClosing(true);
    const t = setTimeout(() => { setMounted(false); setClosing(false); }, reducedMotion() ? 0 : 260);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // pod menu se nescrolluje
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Android Zpět zavře menu místo odchodu ze stránky
  useEffect(() => {
    if (!open) return undefined;
    let popped = false;
    try { window.history.pushState({ ...(window.history.state || {}), ppMenu: true }, ""); } catch { /* noop */ }
    const onPop = () => { popped = true; closeRef.current(); };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (!popped && window.history.state && window.history.state.ppMenu) {
        try { window.history.back(); } catch { /* noop */ }
      }
    };
  }, [open]);

  useEffect(() => {
    if (open && focusSearch) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, focusSearch]);

  const sections = useMemo(() => {
    const out = [];
    let cur = { head: "", rows: [] };
    (items || []).forEach((e) => {
      if (e.group) {
        if (cur.rows.length) out.push(cur);
        out.push({ head: e.label, rows: e.items });
        cur = { head: "", rows: [] };
      } else cur.rows.push(e);
    });
    if (cur.rows.length) out.push(cur);
    return out;
  }, [items]);

  if (!mounted) return null;

  const nq = norm(q.trim());
  const hit = (it) => !nq || norm(`${it.label} ${KW[it.to] || ""}`).includes(nq);
  const shown = sections.map((s) => ({ ...s, rows: s.rows.filter(hit) })).filter((s) => s.rows.length);
  const extraHit = (txt) => !!nq && norm(txt).includes(nq);   // účet jen při hledání (349)
  const showSet = showSettings && extraHit("Nastavení účet profil heslo");
  const showSa = showSuperadmin && extraHit("Superadmin");
  const showDark = extraHit("Tmavý režim noc dark světlý");
  const showOut = extraHit("Odhlásit se odhlášení");
  const loading = !!nq && found.projects === null;
  const pr = nq && found.projects ? searchProjects(found.projects, q) : { rows: [], total: 0 };
  const pe = nq && peopleSearch && found.people ? searchPeople(found.people, q) : { rows: [], total: 0 };
  const empty = nq && !loading && !pr.total && !pe.total && !shown.length && !showSet && !showSa && !showDark && !showOut;
  const results = [
    pr.total ? { head: "Zakázky", kind: "p", ...pr } : null,
    pe.total ? { head: "Lidé", kind: "u", ...pe } : null,
  ].filter(Boolean);

  const tap = (fn) => () => { if (swiped.current) { swiped.current = false; return; } fn(); };
  const onDown = (e) => { swipe.current = { x: e.clientX, y: e.clientY }; };
  const onUp = (e) => {
    const s = swipe.current; swipe.current = null;
    if (!s) return;
    const dx = e.clientX - s.x, dy = Math.abs(e.clientY - s.y);
    if (dx > 80 && dy < 60) { swiped.current = true; onClose(); setTimeout(() => { swiped.current = false; }, 400); }
  };

  const Row = ({ icon: Icon, label, onClick, current, first, last, testid, children, danger, role, checked }) => (
    <button type="button" onClick={tap(onClick)} data-testid={testid} aria-current={current ? "page" : undefined}
      role={role} aria-checked={checked}
      className={`relative flex items-center gap-[13px] w-full h-[54px] px-3.5 bg-card text-left text-[16px] active:bg-secondary ${current ? "font-bold" : "font-semibold"} ${danger ? "text-destructive" : ""} ${first ? "rounded-t-[20px]" : ""} ${last ? "rounded-b-[20px]" : ""}`}>
      {!first && <span aria-hidden="true" className="absolute left-[61px] right-0 top-0 h-px bg-border" />}
      <span aria-hidden="true" className={`w-[34px] h-[34px] rounded-[10px] grid place-items-center shrink-0 ${current ? "bg-brand text-white" : "bg-secondary text-muted-foreground"}`}>
        <Icon size={19} />
      </span>
      <span className="flex-1 min-w-0 truncate">{label}</span>
      {children || <ChevronRight size={16} className="text-muted-foreground/60 shrink-0" aria-hidden="true" />}
    </button>
  );

  return (
    <div role="dialog" aria-modal="true" aria-label="Menu" data-testid="mobile-menu"
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }} onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={() => { swipe.current = null; }}
      className={`md:hidden fixed inset-0 z-[60] bg-background text-foreground flex flex-col shadow-[-14px_0_40px_rgba(15,15,16,.18)] ${closing ? "pp-mb-out" : "pp-mb-in"}`}>
      <div className="flex items-center gap-2 px-3.5 pb-2" style={{ paddingTop: "max(14px, calc(env(safe-area-inset-top) + 6px))" }}>
        <label className="flex-1 min-w-0 h-11 rounded-full bg-card shadow-[0_0_0_1px_hsl(var(--border))] focus-within:shadow-[0_0_0_2px_hsl(var(--accent-orange))] flex items-center gap-2.5 px-4 text-muted-foreground">
          <Search size={18} aria-hidden="true" />
          <input ref={inputRef} type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={peopleSearch ? "Zakázka, člověk nebo modul" : "Zakázka nebo modul"} aria-label="Hledat zakázku, člověka nebo modul"
            autoComplete="off" spellCheck={false} data-testid="mobile-menu-search"
            className="flex-1 min-w-0 h-[42px] bg-transparent border-0 outline-none text-foreground text-[16px] placeholder:text-muted-foreground [appearance:none]" />
        </label>
        <button type="button" onClick={onClose} aria-label="Zavřít menu" data-testid="mobile-menu-close"
          className="w-11 h-11 rounded-full bg-card shadow-[0_0_0_1px_hsl(var(--border))] grid place-items-center shrink-0 active:scale-95 transition-transform">
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain no-scrollbar px-3.5 pb-[calc(34px+env(safe-area-inset-bottom))] pt-1">
        {/* 200: zakázky a lidé — klepnutí otevře jejich kartu */}
        {loading && (
          <div role="status" className="flex items-center gap-2.5 px-1.5 pt-4 pb-1 text-sm text-muted-foreground" data-testid="menu-search-loading">
            <Loader2 size={16} className="animate-spin motion-reduce:animate-none" aria-hidden="true" /> Hledám zakázky{peopleSearch ? " a lidi" : ""}…
          </div>
        )}
        {results.map((g) => (
          <div key={g.head} data-testid={`menu-results-${g.kind}`}>
            <div className="text-[12px] font-bold tracking-[.08em] uppercase text-muted-foreground px-1.5 pt-4 pb-[7px]">{g.head}</div>
            {g.rows.map((r, i) => (
              <button key={r.id} type="button" onClick={tap(() => onNavigate(r.to))} data-testid={`menu-hit-${g.kind}-${r.id}`}
                className={`relative flex items-center gap-[13px] w-full min-h-[58px] py-2 px-3.5 bg-card text-left active:bg-secondary ${i === 0 ? "rounded-t-[20px]" : ""} ${i === g.rows.length - 1 ? "rounded-b-[20px]" : ""}`}>
                {i > 0 && <span aria-hidden="true" className="absolute left-[61px] right-0 top-0 h-px bg-border" />}
                {g.kind === "u"
                  ? <Avatar name={r.name} photo={r.photo} size={34} className="bg-secondary text-muted-foreground text-xs font-bold" />
                  : <span aria-hidden="true" className="w-[34px] h-[34px] rounded-[10px] grid place-items-center shrink-0 bg-secondary text-muted-foreground"><Briefcase size={19} /></span>}
                <span className="flex-1 min-w-0">
                  <span className="block text-[16px] font-semibold truncate">{r.name}</span>
                  {r.sub && <span className="block text-[13px] text-muted-foreground truncate">{r.sub}</span>}
                </span>
                <ChevronRight size={16} className="text-muted-foreground/60 shrink-0" aria-hidden="true" />
              </button>
            ))}
            {g.total > g.rows.length && <div className="px-1.5 pt-1.5 text-[13px] text-muted-foreground">a další {g.total - g.rows.length} — upřesni hledání</div>}
          </div>
        ))}
        {nq && results.length > 0 && shown.length > 0 && <div className="text-[12px] font-bold tracking-[.08em] uppercase text-muted-foreground px-1.5 pt-4 -mb-2">Moduly</div>}

        {shown.map((s, si) => (
          <div key={`${s.head}-${si}`}>
            {s.head ? <div className="text-[12px] font-bold tracking-[.08em] uppercase text-muted-foreground px-1.5 pt-4 pb-[7px]">{s.head}</div> : <div className="h-3" />}
            {s.rows.map((it, i) => (
              <Row key={it.to} icon={it.icon} label={it.label} current={isItemActive(it)} first={i === 0} last={i === s.rows.length - 1}
                testid={`menu-${it.testid}`} onClick={() => onNavigate(it.to)} />
            ))}
          </div>
        ))}

        {(showSet || showSa) && (
          <div className="mt-3">
            {showSet && <Row icon={SettingsIcon} label="Nastavení" first last={!showSa} testid="menu-nav-settings" onClick={onSettings} />}
            {showSa && <Row icon={Shield} label="Superadmin" first={!showSet} last testid="menu-nav-superadmin" onClick={onSuperadmin} />}
          </div>
        )}

        {showDark && (
          <div className="mt-3">
            <Row icon={Moon} label="Tmavý režim" first last role="switch" checked={!!dark} testid="menu-theme-switch" onClick={onToggleDark}>
              <span aria-hidden="true" className={`relative w-12 h-7 rounded-full shrink-0 transition-colors ${dark ? "bg-success" : "bg-muted-foreground/30"}`}>
                <span className={`absolute top-[3px] left-[3px] w-[22px] h-[22px] rounded-full bg-white shadow transition-transform ${dark ? "translate-x-5" : ""}`} />
              </span>
            </Row>
          </div>
        )}

        {showOut && (
          <button type="button" onClick={tap(onLogout)} data-testid="menu-logout"
            className="mt-3 w-full h-[54px] rounded-[20px] bg-card flex items-center justify-center gap-2.5 text-[16px] font-semibold text-destructive active:bg-secondary">
            <LogOut size={18} aria-hidden="true" /> Odhlásit se
          </button>
        )}

        {empty && (
          <div role="status" className="mt-3 bg-card rounded-[20px] px-[18px] py-[22px] text-center">
            <div className="text-[16px] font-bold">Nic jsme nenašli</div>
            <div className="mt-1 text-[14px] text-muted-foreground">{peopleSearch ? "Zkus název zakázky, jméno nebo modul, třeba „úkoly“." : "Zkus název zakázky nebo modul, třeba „úkoly“."}</div>
          </div>
        )}
      </div>
    </div>
  );
}
